if (!customElements.get('rs7gt-story')) {
  customElements.define('rs7gt-story', class extends HTMLElement {
    connectedCallback() {
      this.progress = null;
      this.completedCycles = 0;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener('change', () => this.requestMeasure(), options);
      this.addEventListener('click', event => this.navigate(event), options);
      window.addEventListener('scroll', () => this.requestRender(), { ...options, passive: true });
      this.addEventListener('rs7gt:collage-measure', () => this.requestMeasure(), options);
      this.addEventListener('focusin', event => {
        if (this.heroSignup && this.signup.contains(event.target)) {
          const box = event.target.getBoundingClientRect();
          const covering = this.pageLoop && window.scrollY >= this.coverStart;
          if (!covering && ((this.pageLoop && window.scrollY >= this.collageStart) || box.top < 0 || box.bottom > this.height)) {
            window.scrollTo({ top: this.signupPosition(), behavior: 'instant' });
            if (this.motionEnabled) {
              this.targets = this.getTargets();
              this.progress = { ...this.targets };
              this.paint();
            }
          }
        }
      }, options);
      window.addEventListener('resize', () => this.requestMeasure(), options);
      window.addEventListener('pageshow', () => this.requestMeasure(), options);
      window.addEventListener('load', () => this.requestMeasure(), options);
      ['popstate', 'hashchange'].forEach(name => {
        window.addEventListener(name, () => this.syncNavigation(window.location.hash, true), options);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          cancelAnimationFrame(this.renderFrame);
          this.renderFrame = 0;
        } else this.requestMeasure();
      }, options);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
        document.addEventListener(name, () => this.requestMeasure(), options);
      });
      this.resizeObserver = new ResizeObserver(() => this.requestMeasure());
      this.resizeObserver.observe(this);
      this.sectionsObserver = new MutationObserver(() => this.requestMeasure());
      this.sectionsObserver.observe(this, { childList: true });
      document.fonts?.ready.then(() => {
        if (!options.signal.aborted) this.requestMeasure();
      });
      this.measure();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.resizeObserver.disconnect();
      this.sectionsObserver.disconnect();
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.renderFrame);
      this.measureFrame = this.renderFrame = 0;
      this.removeAttribute('data-motion');
      this.removeAttribute('data-stacked');
      this.removeAttribute('data-page-loop');
      this.removeAttribute('data-loop-cover');
      this.style.removeProperty('--story-hero-height');
      this.style.removeProperty('--story-sticky-top');
      this.collage?.style.removeProperty('--collage-loop-tail');
      this.clearStyles();
    }

    requestMeasure() {
      if (this.measureFrame || !this.isConnected) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measure();
      });
    }

    measure() {
      const previousMotion = this.motionEnabled;
      const previousLoop = this.pageLoop;
      const previousHeroHeight = this.heroHeight;
      const previousHero = this.hero;
      this.hero = this.querySelector('rs7gt-landing');
      if (previousHero !== this.hero) this.heroSceneVisible = undefined;
      this.collage = this.querySelector('rs7gt-collage');
      this.signup = this.querySelector('rs7gt-signup');
      this.heroSignup = this.hero?.contains(this.signup);
      this.navigation = this.hero?.querySelector('.rs7gt__navigation');
      this.viewportHeight = window.innerHeight;
      this.height = this.viewportHeight;
      this.amplitude = window.innerWidth < 750 ? 0.5 : 1;
      this.motionEnabled = !this.motionQuery.matches && this.dataset.designMode !== 'true';
      const heroSection = this.hero?.closest('.rs7gt-section');
      this.pageLoop = this.motionEnabled && this.hero?.dataset.pageLoop === 'true' && Boolean(this.collage?.viewport) && heroSection === this.firstElementChild;
      this.toggleAttribute('data-page-loop', this.pageLoop);
      if (this.pageLoop) {
        this.write(this, '--story-hero-height', CSS.supports('height', '100svh') ? '100svh' : `${this.height}px`);
        this.height = parseFloat(getComputedStyle(this.hero).minHeight) || this.height;
        this.heroHeight = this.hero.offsetHeight;
        this.stickyTop = Math.min(0, this.height - this.heroHeight);
        this.write(this, '--story-sticky-top', `${this.stickyTop}px`);
        this.write(this.collage, '--collage-loop-tail', `${this.heroHeight}px`);
      } else {
        this.collage?.style.removeProperty('--collage-loop-tail');
        this.style.removeProperty('--story-hero-height');
        this.style.removeProperty('--story-sticky-top');
        this.removeAttribute('data-loop-cover');
        this.hero?.style.removeProperty('--story-loop-y');
      }
      this.positions = new Map([this.hero, this.collage, this.signup].filter(Boolean).map(element => {
        const box = element.getBoundingClientRect();
        return [element, { top: box.top + window.scrollY, height: box.height }];
      }));
      this.stacked = this.motionEnabled && this.height >= 600 && heroSection === this.firstElementChild && this.positions.get(this.hero)?.height <= this.height + 1;
      if (heroSection === this.firstElementChild && this.hero) {
        this.positions.get(this.hero).top = this.getBoundingClientRect().top + window.scrollY;
      }
      if (this.pageLoop) {
        this.storyTop = this.positions.get(this.hero).top;
        this.collageStart = this.storyTop + this.heroHeight;
        this.coverStart = this.collageStart + Math.max(0, this.collage.travel || 0);
        this.coverDistance = Math.max(1, this.height - Math.max(0, this.viewportHeight - this.heroHeight));
        this.cycleEnd = this.coverStart + this.coverDistance;
        this.positions.set(this.collage, { top: this.collageStart, height: this.collage.offsetHeight });
      }
      if (previousLoop !== this.pageLoop || previousHeroHeight !== this.heroHeight) this.collage?.requestMeasure?.();
      this.toggleAttribute('data-stacked', this.stacked);
      this.querySelectorAll('[data-story-link]').forEach(link => {
        if (link.hasAttribute('data-photo-link')) link.setAttribute('href', this.collage ? '#RS7GTPhotos' : '#RS7GTSignup');
        link.hidden = !this.querySelector(link.getAttribute('href'));
      });
      this.toggleAttribute('data-motion', this.motionEnabled);
      if (this.motionEnabled) {
        this.targets = this.getTargets();
        if (!this.progress || previousMotion !== this.motionEnabled || previousLoop !== this.pageLoop) {
          this.progress = { ...this.targets };
          this.paint();
        } else this.requestRender();
      } else {
        cancelAnimationFrame(this.renderFrame);
        this.renderFrame = 0;
        this.clearStyles();
      }
    }

    getTargets(y = window.scrollY) {
      const clamp = value => Math.max(0, Math.min(1, value));
      const targets = { hero: 0, collage: 1, signup: 1, cover: 0 };
      if (this.hero) {
        const bounds = this.positions.get(this.hero);
        const reading = this.pageLoop ? Math.max(0, this.heroHeight - this.height) : 0;
        targets.hero = clamp((y - bounds.top - reading) / ((this.pageLoop ? this.height : bounds.height) * 0.9));
      }
      if (this.pageLoop) targets.cover = y >= this.cycleEnd - 1 ? 1 : clamp((y - this.coverStart) / this.coverDistance);
      ['collage', 'signup'].forEach(name => {
        if (this[name] && !(name === 'signup' && this.heroSignup)) targets[name] = clamp((y + this.height - this.positions.get(this[name]).top) / (this.height * 0.92));
      });
      return targets;
    }

    rebaseCycle() {
      this.completedCycles++;
      if (this.collage.contains(document.activeElement)) document.getElementById('MainContent')?.focus({ preventScroll: true });
      if (window.location.hash === '#RS7GTPhotos') history.replaceState(history.state, '', window.location.pathname + window.location.search);
      window.scrollTo({ top: this.storyTop, behavior: 'instant' });
      this.targets = this.getTargets(this.storyTop);
      this.progress = { ...this.targets };
      this.collage.viewport.scrollLeft = 0;
      this.collage.render(this.storyTop);
      this.paint(this.storyTop);
    }

    signupPosition() {
      if (!this.heroSignup) return this.positions.get(this.signup)?.top || 0;
      const localTop = this.signup.getBoundingClientRect().top - this.hero.getBoundingClientRect().top;
      const top = localTop - Math.max(0, (this.height - this.signup.offsetHeight) / 2);
      return this.positions.get(this.hero).top + Math.max(0, Math.min(top, this.hero.offsetHeight - this.height));
    }

    requestRender() {
      if (!this.motionEnabled || document.hidden) return;
      if (this.renderFrame) return;
      this.lastFrameTime = performance.now();
      this.renderFrame = requestAnimationFrame(time => this.tick(time));
    }

    tick(time) {
      this.renderFrame = 0;
      if (!this.isConnected || !this.motionEnabled || document.hidden) return;
      const y = window.scrollY;
      this.targets = this.getTargets(y);
      const delta = Math.min(0.05, (time - this.lastFrameTime) / 1000);
      this.lastFrameTime = time;
      const alpha = 1 - Math.exp(-delta / 0.1);
      let moving = false;
      Object.keys(this.targets).forEach(name => {
        this.progress[name] += (this.targets[name] - this.progress[name]) * alpha;
        if (Math.abs(this.targets[name] - this.progress[name]) < 0.001) this.progress[name] = this.targets[name];
        else moving = true;
      });
      this.paint(y);
      if (moving) this.renderFrame = requestAnimationFrame(next => this.tick(next));
    }

    paint(y = window.scrollY) {
      const ease = value => 1 - (1 - Math.max(0, Math.min(1, value))) ** 3;
      const covering = this.pageLoop && y >= this.coverStart;
      this.toggleAttribute('data-loop-cover', covering);
      if (this.pageLoop) this.write(this.hero, '--story-loop-y', covering ? `${(this.viewportHeight * (1 - this.progress.cover) - this.stickyTop).toFixed(2)}px` : '0px');
      if (this.hero) {
        const bounds = this.positions.get(this.hero);
        this.setHeroVisible(covering || (y + this.height > bounds.top && y < (this.pageLoop ? this.collageStart : bounds.top + bounds.height)));
        const progress = covering ? 0 : this.progress.hero;
        this.write(this.hero, '--story-shift', `${(-progress * 72 * this.amplitude).toFixed(2)}px`);
        this.write(this.hero, '--story-opacity', (1 - progress * 0.95).toFixed(3));
        this.write(this.hero, '--story-media-scale', (1 + progress * 0.035).toFixed(4));
        this.write(this.hero, '--story-cue-opacity', Math.max(0, 1 - progress * 5).toFixed(3));
        if (this.navigation) {
          const inert = !covering && (this.stacked || this.pageLoop) && this.targets.hero > 0.1;
          if (this.navigation.inert !== inert) this.navigation.inert = inert;
        }
      }
      if (this.collage) {
        for (let index = 0; index < 6; index++) {
          const phase = ease((this.progress.collage - index * 0.045) / 0.775);
          this.write(this.collage, `--story-photo-${index}`, phase.toFixed(4));
        }
        this.write(this.collage, '--story-photo-distance', `${110 * this.amplitude}px`);
      }
      if (this.signup) {
        const copy = ease(this.progress.signup / 0.8);
        const panel = ease((this.progress.signup - 0.16) / 0.84);
        this.write(this.signup, '--story-copy-y', `${((1 - copy) * 64 * this.amplitude).toFixed(2)}px`);
        this.write(this.signup, '--story-copy-opacity', (0.15 + copy * 0.85).toFixed(3));
        this.write(this.signup, '--story-panel-y', `${((1 - panel) * 96 * this.amplitude).toFixed(2)}px`);
        this.write(this.signup, '--story-panel-scale', (0.97 + panel * 0.03).toFixed(4));
        this.write(this.signup, '--story-panel-opacity', (0.1 + panel * 0.9).toFixed(3));
      }
      if (covering && this.progress.cover === 1 && y >= this.cycleEnd - 1) this.rebaseCycle();
    }

    write(element, name, value) {
      if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
    }

    setHeroVisible(visible) {
      if (!this.hero || this.heroSceneVisible === visible) return;
      this.heroSceneVisible = visible;
      this.hero.dispatchEvent(new CustomEvent('rs7gt:visibilitychange', { detail: { visible } }));
    }

    clearStyles() {
      [this.hero, this.collage, this.signup].filter(Boolean).forEach(element => {
        Array.from(element.style).filter(name => name.startsWith('--story-')).forEach(name => element.style.removeProperty(name));
      });
      if (this.navigation) this.navigation.inert = false;
      this.setHeroVisible(true);
    }

    syncNavigation(hash, restoreFocus = false) {
      if (hash === '#RS7GTSignup') this.collage?.dispatchEvent(new Event('rs7gt:leave-collage'));
      if (hash === '#RS7GTPhotos') this.collage?.dispatchEvent(new Event('rs7gt:enter-collage'));
      if (!restoreFocus) return;
      let target;
      if (hash === '#RS7GTPhotos') target = this.collage?.querySelector('[data-collage-viewport]');
      else if (hash === '#RS7GTSignup') target = this.signup;
      else if (hash === '#RS7GTStart') target = this.hero;
      else if (!hash) target = document.getElementById('MainContent');
      target?.focus({ preventScroll: true });
    }

    navigate(event) {
      const link = event.target.closest('[data-story-link]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || link.target || link.hasAttribute('download')) return;
      const hash = link.getAttribute('href');
      const target = this.querySelector(hash);
      if (!target) return;
      event.preventDefault();
      this.syncNavigation(hash);
      const focusTarget = target.querySelector('[data-collage-viewport]') || target;
      focusTarget.focus({ preventScroll: true });
      const top = hash === '#RS7GTSignup' ? this.signupPosition() : this.positions.get(target)?.top;
      if (top !== undefined) window.scrollTo({ top, behavior: this.motionQuery.matches ? 'instant' : 'smooth' });
      if (window.location.hash !== hash) history.pushState(null, '', hash);
    }
  });
}
